import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Button,
  Stack,
  TextField,
  MenuItem,
  FormControl,
  Grid,
  Typography,
} from '@mui/material';

// COMPONENTS
import AllMessagesTable from '../../components/reports/allmessages/AllMessagesTable';
import AllMessagesSingle from '../../components/reports/allmessages/AllMessagesSingle';
import Layout from '../../components/layouts/Layout';
import Loader from '../../components/common/Loader';
import Paginator from '../../components/common/Paginator';
import DateRangeSelector from '../../components/common/DateRangeSelector';
import AdvancedFiltersContainer from '../../components/common/AdvancedFiltersContainer';

// CONFIG
import config from '../../Config';

// I18N
import AllMessagesFormattedMessages from '../../messages/reports/AllMessagesFormattedMessages';

// NETWORK
import useAllMessagesRequest from '../../requests/AllMessagesRequest';
import { ReportsRoutes } from '../../routes/ReportsRoutes';

const AllMessages = ({ location, errorHandler, accessLevel, accessType }) => {
  const [sent, setSent] = useState([]);
  const [paginatedSent, setPaginatedSent] = useState([]);
  const [selected, setSelected] = useState(null);
  const [redirectFromReply, setRedirectFromReply] = useState(null);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [timeRange, setTimeRange] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLength, setPageLength] = useState(config.defaultPL);
  const [currentlySearching, setCurrentlySearching] = useState({});
  const [open, setOpen] = useState(false);
  const [selectorKeyRefresh, setSelectorKeyRefresh] = useState(false);
  const [cancelList, setCancelList] = useState(new Set());
  const [rowCount, setRowCount] = useState(0);
  const [filteredText, setFilteredText] = useState([]);
  const [reset, setReset] = useState(false);
  const [filters, setFilters] = useState({
    fromFilter: '',
    toFilter: '',
    userFilter: '',
    statusFilter: '',
    errorFilter: '',
  });
  const { searchAllSends } = useAllMessagesRequest();

  // Add debounce timer ref
  const debounceTimer = useRef(null);

  useEffect(() => {
    if (location?.state?.redirectFromReply) {
      setSelected(location?.state?.originalMessage);
      setRedirectFromReply(location?.state?.redirectFromReply);
    }
  }, [location]);

  /*
    Back button
  */
  const backButtonClick = () => {
    setSelected(null);
    setRedirectFromReply(null);
  };

  /*
    Handles searching User Data between two dates
  */
  const handleUserDataSearch = useCallback(async () => {
    try {
      setIsSearching(true);
      setSent([]);

      const searchParams = {
        ...filters,
        fromDate: fromDate,
        toDate: toDate,
        timeRangeStart: fromDate,
        timeRangeEnd: toDate,
        type: 'TXT',
        timeRange: timeRange,
        pageNumber: pageNumber,
        pageLength: pageLength,
      };

      const sentData = await searchAllSends(searchParams, errorHandler);
      setSent(sentData.items);
      setCurrentlySearching(searchParams);
      setIsSearching(false);
      setCancelList(new Set());
      setRowCount(sentData.rowCount);
      setPaginatedSent(sentData.items);
    } catch (e) {
      console.error(e);
      setError(e.message);
      setIsSearching(false);
      setCancelList(new Set());
    }
  }, [toDate, fromDate, pageNumber, pageLength, reset]);

  useEffect(() => {
    if (fromDate && toDate) handleUserDataSearch();
  }, [handleUserDataSearch]);

  /*
    Handles the selection and display of a single Sent SMS
  */
  const showSingleSentSms = (sms) => {
    console.log('single:', sms);
    setSelected(sms);
  };

  /*
    Handles the selection and display of a single Sent SMS
  */
  const handleClickToFilter = (name, value) => {
    if (!value) return;

    // Create new filters object
    const newFilters = { ...filters, [name]: value };

    // Update state
    setFilters(newFilters);
    setOpen(true);
    setPageNumber(1);
    setReset(!reset);
  };

  const statusDropdown = () => {
    const sendStatus = [
      'Blacklisted',
      'Cancelled',
      'Delivered',
      'Failed Delivery',
      'Invalid Number',
      'Pending',
      'Presumed Delivered',
      'Processing',
      'Scheduled',
      'Too Many Retries',
    ];

    return (
      <FormControl fullWidth variant="standard">
        <TextField
          select
          id="statusFilter"
          label="Status"
          name="statusFilter"
          value={filters.statusFilter}
          onChange={handleFilterChange}
          size="small"
          placeholder="Select Status"
        >
          <MenuItem value="">
            <em>Select Status</em>
          </MenuItem>
          {sendStatus.map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </TextField>
      </FormControl>
    );
  };

  /*
      Generates the Search Tools for querying the data
    */
  const generateSearchText = useCallback(() => {
    // Destructures which filters the table is currently displaying fields on.
    const {
      typeFilter,
      userFilter,
      fromFilter,
      toFilter,
      statusFilter,
      errorFilter,
    } = currentlySearching || {};

    const filterText = [];
    const filterValues = ['Type:', 'User:', 'From:', 'To:', 'Status:', 'Error'];

    [
      typeFilter,
      userFilter,
      fromFilter,
      toFilter,
      statusFilter,
      errorFilter,
    ].forEach((filter, i) => {
      if (filter && filter !== '')
        filterText.push(
          <span key={i}>
            {filterText && filterText.length === 0 ? '' : ', '}
            <strong>{filterValues[i]}</strong> {filter}
          </span>
        );
    });

    setFilteredText(filterText);
  }, [currentlySearching]);

  useEffect(() => {
    generateSearchText();
  }, [generateSearchText]);

  /* Changes the amount of entries per pagination page. */
  const changePageLength = (e) => {
    const value = e.target.value;
    setPageLength(value);
    setPageNumber(1);
  };

  /* Redos call to API backend to get refreshed results */
  const reloadPage = () => {
    console.log('reloadPage');
    handleUserDataSearch(fromDate, toDate, timeRange);
  };

  useEffect(() => {
    setPaginatedSent(sent);
  }, [sent]);

  /**
   * Handles the change event when clicking a pagination number
   */
  const changeEvent = async (i) => {
    setPageNumber(i);
  };

  /*
    Convenience function to handle the updating of a form element
  */
  const handleFilterChange = (event) => {
    const target = event.target;
    const name = target.name;
    const value = target.type === 'checkbox' ? target.checked : target.value;

    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);

    // Clear any existing timer
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    // Set new timer to trigger search after 500ms
    debounceTimer.current = setTimeout(() => {
      setPageNumber(1);
      setReset(!reset);
    }, 500);
  };

  const clearFilters = async () => {
    // Create empty filters
    const emptyFilters = {
      fromFilter: '',
      toFilter: '',
      typeFilter: '',
      userFilter: '',
      statusFilter: '',
      errorFilter: '',
    };

    // Update state
    setFilters(emptyFilters);
    setPageNumber(1);
    setReset(!reset);
  };

  if (accessLevel === '3' && accessType === '3') {
    return (
      <Layout
        routes={ReportsRoutes}
        title={
          selected
            ? 'SMS Information'
            : AllMessagesFormattedMessages.allMessagesTitle
        }
        accessLevel={accessLevel}
        accessType={accessType}
        backButtonText={selected ? AllMessagesFormattedMessages.backText : null}
        handleBackButton={() => setSelected(null)}
      >
        {selected || redirectFromReply ? (
          <AllMessagesSingle
            sms={selected}
            redirectFromReply={redirectFromReply}
            backButtonClick={backButtonClick}
            errorHandler={errorHandler}
          />
        ) : (
          <Grid container sx={{ justifyContent: 'space-between', rowGap: 2 }}>
            <Grid size={{ xs: 12, lg: 9 }}>
              <DateRangeSelector
                key={selectorKeyRefresh ? 1 : 0}
                handleDateSearch={handleUserDataSearch}
                parentStartDate={fromDate}
                setParentStartDate={setFromDate}
                parentEndDate={toDate}
                setParentEndDate={setToDate}
              />
              <AdvancedFiltersContainer open={open} setOpen={setOpen}>
                <form id="filter-form">
                  <Stack
                    sx={{
                      flexDirection: { lg: 'row' },
                      gap: 2,
                      marginY: 2,
                      maxWidth: { lg: 674 },
                    }}
                  >
                    <Stack>
                      <Typography sx={{ whiteSpace: 'nowrap', marginTop: 1 }}>
                        By Field:
                      </Typography>
                    </Stack>

                    <Stack sx={{ gap: 1, flex: 1 }}>
                      <Stack
                        sx={{
                          flexDirection: { lg: 'row' },
                          gap: 2,
                          alignItems: { lg: 'center' },
                        }}
                      >
                        <TextField
                          name="userFilter"
                          id="userFilter"
                          label="User"
                          fullWidth
                          size="small"
                          onChange={handleFilterChange}
                          value={filters.userFilter}
                        />
                        <TextField
                          name="fromFilter"
                          id="fromFilter"
                          label="From"
                          fullWidth
                          size="small"
                          onChange={handleFilterChange}
                          placeholder="Search Number"
                          value={filters.fromFilter}
                        />

                        <TextField
                          name="toFilter"
                          id="toFilter"
                          label="To"
                          fullWidth
                          size="small"
                          onChange={handleFilterChange}
                          placeholder="Search Number"
                          value={filters.toFilter || ''}
                        />
                      </Stack>
                      <Stack
                        sx={{
                          flexDirection: { lg: 'row' },
                          gap: 2,
                          alignItems: { lg: 'center' },
                        }}
                      >
                        <TextField
                          name="errorFilter"
                          id="errorFilter"
                          label="Error"
                          fullWidth
                          size="small"
                          onChange={handleFilterChange}
                          placeholder="Search Code"
                          value={filters.errorFilter}
                        />

                        {statusDropdown()}
                      </Stack>
                    </Stack>
                  </Stack>
                </form>
              </AdvancedFiltersContainer>
              <Stack
                sx={{
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 2,
                  maxWidth: { lg: 674 },
                }}
              >
                {filteredText && filteredText.length > 0 && (
                  <Button
                    color="secondary"
                    onClick={clearFilters}
                    sx={{
                      textTransform: 'none',
                      textDecoration: 'underline',
                      fontWeight: 400,
                    }}
                  >
                    {AllMessagesFormattedMessages.clear}
                  </Button>
                )}
              </Stack>
            </Grid>

            <Grid size={12}>
              {isSearching ? (
                <Loader />
              ) : sent.length === 0 || paginatedSent.length === 0 ? (
                <Typography>No results found.</Typography>
              ) : (
                <>
                  <Stack>
                    {filteredText && filteredText.length > 0 && (
                      <Typography fontSize={12} marginBottom={1}>
                        {filteredText}
                      </Typography>
                    )}
                  </Stack>
                  <AllMessagesTable
                    list={paginatedSent}
                    showSingleSentSms={showSingleSentSms}
                    clickToFilter={handleClickToFilter}
                  />
                  <Paginator
                    itemCount={rowCount}
                    changeEvent={changeEvent}
                    pageLength={pageLength}
                    pageNumber={pageNumber}
                    editFunction={changePageLength}
                    showTotal={'Total Sent Messages Found'}
                  />
                </>
              )}
            </Grid>
          </Grid>
        )}
      </Layout>
    );
  }
  return null;
};

export default AllMessages;
